import { useQueryDocumentById } from '@/hooks/query/useQueryDocumentById';
import { TabType } from '@/hooks/query/useQuerySessionWindows';
import { useActiveTabData } from '@/hooks/useActiveTabData';
import {
  type Brain,
  type DocumentTabType,
  GenerationMethod,
  type VoiceStyle,
} from '@/types/schema';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { type ConsultNoteStatus, DocumentGenType } from '@heidi/schema/ml-scribe';
import type { CustomStyle } from '@heidi/session/contracts';
import { type StreamingStatus } from '@heidi/session/contracts';
import { useQuerySessionById } from '@heidi/session/data';

type ActiveNoteArtifact = {
  kind: 'note';
  sessionId: string;
  status: ConsultNoteStatus | null;
  streamingStatus?: StreamingStatus | null;
  shouldShowNoteSyncChanges?: boolean;
  generateOutputWithoutRecording?: boolean;
  documentId: null;
  templateId: string | null;
  generationMethod: GenerationMethod | null;
  aiCommandText: string | null;
  voiceStyle: VoiceStyle | null;
  customStyle: CustomStyle | null;
  brain: Brain | null;
  updatedAt: string | null;
  markdownContent: string | null;
  fallbackName: null;
};

type ActiveDocumentArtifact = {
  kind: 'document';
  sessionId: string;
  documentId: string;
  templateId: string | null;
  documentTabType: DocumentTabType | null;
  streamingStatus?: StreamingStatus | null;
  shouldShowDocumentSyncChanges?: boolean;
  generateOutputWithoutRecording?: boolean;
  /** Document generation type used to identify Heidi system templates. */
  generationType?: DocumentGenType | null;
  generationTime?: string | null;
  noteUpdatedAt?: string | null;
  generationMethod: null;
  aiCommandText: null;
  voiceStyle: VoiceStyle | null;
  customStyle: CustomStyle | null;
  brain: Brain | null;
  updatedAt: string | null;
  markdownContent: string | null;
  fallbackName: string | null;
  /** Kinde user id of the clinician who created this sub-document, used to gate
   * regenerate when reviewing another user's primary document (V1 parity). */
  creatorKindeId: string | null;
};

export type ActiveArtifact = ActiveNoteArtifact | ActiveDocumentArtifact;

export type ArtifactTabTarget = { tabName: TabType | null; documentId: string };

/**
 * Resolves the artifact for an explicit tab target, for surfaces without an
 * active tab such as the Work RHS and future split-screen panes.
 *
 * The target maps as follows:
 *  - `TabType.NOTE` → the session's consult-note.
 *  - `TabType.DOCUMENT` → the selected sub-document.
 *  - `TabType.CONTEXT` / `TabType.TRANSCRIPT` → returns `null`.
 *
 * The discriminated union lets session-scoped rows (style, last-edited,
 * copy, export) read fields blindly, while rows that act differently per
 * artifact (Change template re-templates the note vs. the document via
 * separate mutations; Regenerate) branch on `kind`.
 */
export const useArtifactForTab = (
  sessionId: string,
  target: ArtifactTabTarget
): ActiveArtifact | null => {
  const { tabName, documentId } = target;

  const { data: sessionArtifact } = useQuerySessionById({
    sessionId,
    select: (data) => {
      const note = data?.consult_note;
      return {
        noteArtifact: note
          ? {
              status: note.status ?? null,
              templateId: note.template_id ?? null,
              generationMethod: note.generation_method ?? null,
              aiCommandText: note.ai_command_text ?? null,
              voiceStyle: note.voice_style ?? null,
              customStyle: note.custom_style ?? null,
              brain: note.brain ?? null,
              updatedAt: note.updated_at ?? null,
              markdownContent: note.result ?? null,
            }
          : null,
        streamingStatus: data?.streamingStatus ?? null,
        generateOutputWithoutRecording: data?.generate_output_without_recording ?? false,
        shouldShowNoteSyncChanges: data?.shouldShowNoteSyncChanges ?? false,
        shouldShowDocumentSyncChanges: data?.shouldShowDocumentSyncChanges ?? false,
        noteUpdatedAt: note?.updated_at ?? null,
      };
    },
  });

  const { data: documentArtifact } = useQueryDocumentById({
    sessionId,
    documentId,
    select: (doc) => {
      if (!doc) return null;
      return {
        templateId: doc.template_id ?? null,
        documentTabType: doc.document_tab_type ?? null,
        streamingStatus: doc.streamingStatus ?? null,
        generationType: doc.generation_type ?? null,
        generationTime: doc.generation_time ?? null,
        voiceStyle: doc.voice_style ?? null,
        customStyle: doc.custom_style ?? null,
        brain: doc.brain ?? null,
        updatedAt: doc.updated_at ?? null,
        markdownContent: doc.content ?? null,
        fallbackName: doc.name ?? null,
        creatorKindeId: doc.creator_kinde_id ?? null,
      };
    },
  });

  const noteArtifact = sessionArtifact?.noteArtifact;

  if (!sessionId) return null;

  if (tabName === TabType.NOTE && noteArtifact) {
    return {
      kind: 'note',
      sessionId,
      documentId: null,
      fallbackName: null,
      streamingStatus: sessionArtifact?.streamingStatus ?? null,
      shouldShowNoteSyncChanges: sessionArtifact?.shouldShowNoteSyncChanges ?? false,
      generateOutputWithoutRecording:
        sessionArtifact?.generateOutputWithoutRecording ?? false,
      ...noteArtifact,
    };
  }

  if (tabName === TabType.DOCUMENT && documentArtifact && documentId) {
    return {
      kind: 'document',
      sessionId,
      documentId,
      generationMethod: null,
      aiCommandText: null,
      generateOutputWithoutRecording:
        sessionArtifact?.generateOutputWithoutRecording ?? false,
      shouldShowDocumentSyncChanges:
        sessionArtifact?.shouldShowDocumentSyncChanges ?? false,
      noteUpdatedAt: sessionArtifact?.noteUpdatedAt ?? null,
      ...documentArtifact,
    };
  }

  return null;
};

/**
 * Resolves the artifact backing the V2 Note context card, mirroring whichever
 * tab is active in the session windows cache.
 */
export const useActiveArtifact = (): ActiveArtifact | null => {
  const sessionId = useCurrentSessionId();
  const artifact = useArtifactForTab(sessionId ?? '', useActiveTabData(sessionId ?? ''));

  if (!sessionId) return null;
  return artifact;
};

/**
 * Maps a document's `generation_type` to the `GenerationMethod` its template
 * query needs: built-in (internal) templates hit the internal endpoint,
 * everything else the regular one. Shared with `DocumentTabIcon` so the tab
 * icon and the card resolve the same cached template entry (`generationMethod`
 * is part of the query key) instead of fetching the same template twice.
 */
export const documentTemplateGenerationMethod = (
  generationType: DocumentGenType | null | undefined
): GenerationMethod =>
  generationType === DocumentGenType.INTERNAL_TEMPLATE
    ? GenerationMethod.INTERNAL_TEMPLATE
    : GenerationMethod.TEMPLATE;

/**
 * The generation method to resolve an artifact's template with. Sub-documents
 * hardcode no generation method but carry a `generationType`, mapped via
 * `documentTemplateGenerationMethod` so built-in (internal) note templates
 * resolve their category and name. Notes keep their own generation method.
 */
export const templateGenerationMethodForArtifact = (
  artifact: ActiveArtifact | null
): GenerationMethod | undefined => {
  if (artifact?.kind === 'document') {
    return documentTemplateGenerationMethod(artifact.generationType);
  }
  return artifact?.generationMethod ?? undefined;
};
